/*
 * Referrals: everyone the partner has referred, filtered by status on the
 * server (GET /referrals?status=…). A converted referral shows the client's
 * plan and the commission earned from them; a rejected one shows Wealthoak's
 * reason.
 */

import { useState } from "react";
import { addedUsers, auth, errorMessage, referrals, type Referral, type ReferralSource, type ReferralStatus } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { AddUserButton } from "./AddUserSheet";
import { useLoad } from "../api/useLoad";
import { EmptyState, LoadError, REFERRAL_STATUS, SkeletonRows, Status, accountStatus } from "./bits";
import { fmtDate, inr, plural } from "./format";
import { Plus, UserPlus } from "./icons";
import ReferralSheet from "./ReferralSheet";
import "./clients.css";
import "./referrals.css";

type Filter = "all" | ReferralStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "submitted", label: "Submitted" },
  { key: "contacted", label: "Contacted" },
  { key: "converted", label: "Converted" },
  { key: "rejected", label: "Rejected" },
];

export default function ReferralsWorkspace({ onRefer }: { onRefer: () => void }) {
  const [filter, setFilter] = useState<Filter>("all");
  const list = useLoad(() => referrals.list(filter === "all" ? undefined : filter), [filter]);
  const rows = list.data?.items ?? [];
  const [open, setOpen] = useState<Referral | null>(null);

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Referrals</p>
          <h1>Your referrals</h1>
          <p className="page-heading__sub">
            Everyone you&apos;ve introduced to Wealthoak and where they are. Our team contacts each lead; when we
            open their account, they become your client.
          </p>
        </div>
        <div className="page-actions">
          <AddUserButton onAdded={() => list.reload()} />
          <button className="button button--primary" onClick={onRefer}>
            <Plus size={16} /> Refer a client
          </button>
        </div>
      </section>

      <div className="toolbar">
        <div className="range-tabs" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button key={f.key} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        {list.data && (
          <span className="toolbar__count">{plural(rows.length, "referral")}</span>
        )}
      </div>

      <section className="card table-card" aria-label="Referrals" aria-busy={list.loading}>
        {list.error ? (
          <div className="table-card__state">
            <LoadError message={list.error} onRetry={list.reload} />
          </div>
        ) : !list.data ? (
          <SkeletonRows rows={5} label="Loading referrals" />
        ) : rows.length === 0 ? (
          filter === "all" ? (
            <EmptyState
              icon={UserPlus}
              title="No referrals yet"
              action={
                <button className="button button--primary" onClick={onRefer}>
                  <Plus size={16} /> Refer your first client
                </button>
              }
            >
              Know someone who&apos;d benefit from Wealthoak? Send us their details and we&apos;ll take it from there.
            </EmptyState>
          ) : (
            <EmptyState icon={UserPlus} title={`No ${FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} referrals`}>
              Nothing here right now. Try another filter.
            </EmptyState>
          )
        ) : (
          <table className="data-table is-stacked ref-table">
            <thead>
              <tr>
                <th scope="col">Lead</th>
                <th scope="col">Referred</th>
                <th scope="col">Status</th>
                <th scope="col">Client</th>
                <th scope="col" className="r">Commission</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ReferralRow key={r.id} r={r} onChanged={list.reload} onOpen={() => setOpen(r)} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {open && <ReferralSheet r={open} onClose={() => setOpen(null)} />}
    </>
  );
}

const SOURCE_LABEL: Partial<Record<ReferralSource, string>> = {
  link: "Via your link",
  credit: "Add-user request",
  staff: "Added by Wealthoak",
};

function ReferralRow({ r, onChanged, onOpen }: { r: Referral; onChanged: () => void; onOpen: () => void }) {
  const st = REFERRAL_STATUS[r.status];
  const client = r.client;
  const { setMe } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const source = r.source ? SOURCE_LABEL[r.source] : undefined;

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await addedUsers.cancel(r.id);
      void auth.me().then(setMe).catch(() => undefined);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    // The row is clickable for mouse users; the name button is the keyboard and screen-reader way in.
    <tr className="client-row" onClick={onOpen}>
      <td className="is-title">
        <div className="ref-lead">
          <button type="button" className="client-open" onClick={onOpen} aria-haspopup="dialog">
            {r.lead_name}
          </button>
          <small>
            {r.lead_email}
            {r.lead_phone ? ` · ${r.lead_phone}` : ""}
          </small>
          {r.notes && <small className="ref-note">“{r.notes}”</small>}
          {source && <span className={`chip ref-source is-${r.source}`}>{source}</span>}
        </div>
      </td>
      <td data-label="Referred">
        <span>
          {fmtDate(r.created_at)}
          {r.converted_at && <small>Converted {fmtDate(r.converted_at)}</small>}
        </span>
      </td>
      <td data-label="Status">
        <span className="ref-status">
          <Status tone={st.tone}>{st.label}</Status>
          {r.status === "rejected" && r.status_reason && <small className="ref-reason">{r.status_reason}</small>}
          {r.source === "credit" && r.status === "rejected" && <small className="ref-muted">Credit returned</small>}
          {r.source === "credit" && r.status === "submitted" && (
            <button
              className="link-button"
              onClick={(e) => {
                e.stopPropagation(); // cancelling must not also open the sheet
                void cancel();
              }}
              disabled={busy}
            >
              {busy ? "Cancelling…" : "Cancel request"}
            </button>
          )}
          {error && <small className="ref-reason">{error}</small>}
        </span>
      </td>
      <td data-label="Client">
        {client ? (
          <span className="ref-client">
            <span className={`chip${client.plan_name ? " is-gold" : ""}`}>{client.plan_name ?? "No plan yet"}</span>
            <small>Account {accountStatus(client.account_status).label.toLowerCase()}</small>
          </span>
        ) : (
          <span className="ref-muted">—</span>
        )}
      </td>
      <td data-label="Commission" className="r">
        <span className={Number(r.commission_total) > 0 ? "cell-strong" : "ref-muted"}>
          {r.status === "converted" || Number(r.commission_total) > 0 ? inr(r.commission_total) : "—"}
        </span>
        {Number(client?.commission_in_progress) > 0 && <small>{inr(client?.commission_in_progress)} in progress</small>}
      </td>
    </tr>
  );
}
